import { useState } from 'react';
import { EventRow } from '../components/EventRow';
import { PageHeader } from '../components/PageHeader';
import { ResultSummary } from '../components/ResultSummary';
import { EmptyPanel, ErrorPanel, LoadingPanel } from '../components/StatePanel';
import { TruncationNotice } from '../components/TruncationNotice';
import { useCorroborationQueue, useEvents } from '../hooks/useRadarQuery';

/**
 * The timeline, and the research queue derived from it.
 *
 * The queue is a separate request rather than a filter over the timeline: an
 * event's grade is computed from the sources citing it and is held in no
 * column, so it cannot be narrowed by a query parameter. Filtering the fetched
 * page in the browser would answer "which of the newest events need work" while
 * appearing to answer "which events need work" — and a short queue reads as
 * good news, which is what makes that particular shortfall worth avoiding.
 */
export function EventsPage() {
  const [queued, setQueued] = useState(false);
  const timeline = useEvents();
  const corroboration = useCorroborationQueue(queued);
  const query = queued ? corroboration : timeline;

  if (query.isLoading) return <LoadingPanel />;
  if (query.isError || !query.data) return <ErrorPanel error={query.error} />;
  const { items, total } = query.data;
  return (
    <>
      <PageHeader eyebrow="Longitudinal intelligence" title="Evidence timeline" description="Funding, model releases, validation, deployment, hiring, and negative signals in one auditable chronology." />
      <section className="segmented" role="group" aria-label="Timeline view">
        <button type="button" aria-pressed={!queued} onClick={() => setQueued(false)}>All signals</button>
        <button type="button" aria-pressed={queued} onClick={() => setQueued(true)}>Needs corroboration</button>
      </section>
      {/* Deliberately does not say what is missing: the queue holds events short
          of the top grade for three different reasons, and each row states its
          own. The summary said "fewer than two independent organisations", which
          was false for every event already carrying two and waiting on a primary
          record. */}
      {queued && (
        <ResultSummary
          count={total}
          one="event is short of confirmation."
          many="events are short of confirmation."
        />
      )}
      <TruncationNotice shown={items.length} total={total} noun="events" />
      <section className="panel">
        {items.length
          ? items.map((event) => <EventRow key={event.id} event={event} />)
          : <EmptyPanel message={queued ? 'Every recorded event is confirmed.' : 'No events found.'} />}
      </section>
    </>
  );
}
